<template>
  <el-dialog :title="dialogTitle" :visible.sync="visible" width="620px" top="5vh" :close-on-click-modal="false" @close="handleClose">
    <el-form ref="form" v-loading="loadingDetail" :model="form" :rules="rules" label-width="125px" size="small" :disabled="isView">
      <el-form-item label="卡券名称" prop="couponName">
        <el-input v-model.trim="form.couponName" maxlength="50" show-word-limit />
      </el-form-item>
      <el-form-item label="卡券类型" prop="couponType">
        <el-select v-model="form.couponType" style="width:100%" @change="onTypeChange">
          <el-option v-for="item in couponTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
      </el-form-item>
      <el-form-item v-if="normalCoupon" label="用户类别" prop="userTypeId">
        <el-select v-model="form.userTypeId" filterable style="width:100%">
          <el-option v-for="item in userTypeOptions" :key="item.id" :label="item.typeName" :value="item.id" />
        </el-select>
      </el-form-item>
      <el-form-item v-if="normalCoupon" label="发放时间" prop="grantRange">
        <el-date-picker v-model="form.grantRange" type="datetimerange" value-format="timestamp" start-placeholder="开始时间" end-placeholder="结束时间" style="width:100%" />
      </el-form-item>
      <el-form-item v-if="rateCoupon" label="抵扣比例" prop="deductRate">
        <el-input v-model="form.deductRate" @input="integerInput('deductRate')"><template slot="append">%</template></el-input>
      </el-form-item>
      <el-form-item label="卡券有效期" prop="validDays">
        <el-input v-model="form.validDays" @input="integerInput('validDays')"><template slot="append">天</template></el-input>
      </el-form-item>
      <el-form-item v-if="vipCoupon" label="VIP等级" prop="vipLevel">
        <el-select v-model="form.vipLevel" style="width:100%" placeholder="请选择VIP等级">
          <el-option v-for="item in vipLevelOptions" :key="item.code" :label="item.label" :value="Number(item.code)" />
        </el-select>
      </el-form-item>
      <el-form-item v-if="vipCoupon" label="VIP有效期" prop="vipValidDays">
        <el-input v-model="form.vipValidDays" @input="integerInput('vipValidDays')"><template slot="append">天</template></el-input>
      </el-form-item>
      <el-form-item v-if="!vipCoupon" label="资金账户信息">
        <el-input :value="fundAccountDisplay" disabled />
      </el-form-item>
      <el-form-item v-if="!vipCoupon" label="币种" prop="coin">
        <el-select v-if="tokenCoupon" v-model.trim="form.coin" filterable allow-create default-first-option style="width:100%" placeholder="请选择或输入租户已启用现货币种">
          <el-option v-for="item in coinOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
        <el-input v-else value="USDT" disabled />
      </el-form-item>
      <el-form-item v-if="!vipCoupon" label="卡券金额" prop="amount">
        <el-input v-model="form.amount" @input="amountInput" />
      </el-form-item>
      <template v-if="positionCoupon">
        <el-form-item label="支持交易对" prop="symbolIds">
          <el-select v-model="form.symbolIds" multiple filterable style="width:100%" @change="updateLeverageRange">
            <el-option v-for="item in symbolOptions" :key="item.symbolId" :label="item.enabled === false ? item.symbol + '（已停用）' : item.symbol" :value="item.symbolId" :disabled="item.enabled === false" />
          </el-select>
        </el-form-item>
        <el-form-item label="杠杆倍数" prop="leverage">
          <el-input v-model="form.leverage" :placeholder="leverageHint" @input="integerInput('leverage')"><template slot="append">X</template></el-input>
        </el-form-item>
        <el-form-item label="仓位有效期" prop="positionValidDays">
          <el-input v-model="form.positionValidDays" @input="integerInput('positionValidDays')"><template slot="append">天</template></el-input>
        </el-form-item>
      </template>
    </el-form>
    <div slot="footer">
      <el-button size="small" @click="handleClose">{{ isView ? '关闭' : '取消' }}</el-button>
      <el-button v-if="!isView" size="small" type="primary" :loading="submitting" @click="submit">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import api from '../api'
import userTypeApi from '../../UserType/api'
import {
  COUPON_TYPE, COUPON_TYPE_OPTIONS, COIN_OPTIONS, isNormalCoupon,
  isTokenCoupon, isPositionCoupon, isVipCoupon, hasDeductRate,
} from '../constants'

const TITLES = { add: '添加卡券', edit: '修改卡券', copy: '复制卡券', view: '卡券详情' }
const emptyForm = () => ({
  id: null, couponName: '', couponType: COUPON_TYPE.TASK_EXPERIENCE,
  deductRate: '', validDays: '', vipLevel: null, vipValidDays: '', coin: 'USDT', amount: '', userTypeId: null,
  grantRange: [], symbolIds: [], leverage: '', positionValidDays: '',
  fundAccountId: null, fundAccountType: null, fundAccountName: '',
})

const FUND_ACCOUNT_MAP = {
  EXPERIENCE: { type: 24, name: '体验金账户' },
  FEE_DEDUCT: { type: 25, name: '手续费抵扣券账户' },
  PNL_DEDUCT: { type: 26, name: '盈亏抵扣券账户' },
  TOKEN: { type: 27, name: '代币券账户' },
  POSITION_EXPERIENCE: { type: 28, name: '仓位体验券账户' },
}

export default {
  name: 'CouponFormDialog',
  props: { value: Boolean, mode: { type: String, default: 'add' }, couponId: { type: [Number, String], default: null } },
  data() {
    const positiveInt = (message) => ({ validator: (r, v, cb) => /^[1-9]\d*$/.test(String(v || '')) ? cb() : cb(new Error(message)), trigger: 'blur' })
    return {
      submitting: false, loadingDetail: false, form: emptyForm(),
      couponTypeOptions: COUPON_TYPE_OPTIONS, coinOptions: COIN_OPTIONS,
      userTypeOptions: [], symbolOptions: [], vipLevelOptions: [], leverageMin: 1, leverageMax: 100,
      rules: {
        couponName: [{ required: true, message: '请输入1-50字符卡券名称', trigger: 'blur' }],
        couponType: [{ required: true, message: '请选择卡券类型', trigger: 'change' }],
        userTypeId: [{ required: true, message: '请选择用户类别', trigger: 'change' }],
        grantRange: [{ validator: (r, v, cb) => this.validateRange(v, cb), trigger: 'change' }],
        deductRate: [{ validator: (r, v, cb) => this.validateRate(v, cb), trigger: 'blur' }],
        validDays: [positiveInt('请输入正整数有效期')],
        vipLevel: [{ validator: (r, v, cb) => this.validateVipLevel(v, cb), trigger: 'change' }],
        vipValidDays: [{ validator: (r, v, cb) => this.validateVipValidDays(v, cb), trigger: 'blur' }],
        coin: [{ required: true, message: '请输入币种', trigger: 'change' }],
        amount: [{ validator: (r, v, cb) => this.validateAmount(v, cb), trigger: 'blur' }],
        symbolIds: [{ type: 'array', required: true, min: 1, message: '请选择至少一个交易对', trigger: 'change' }],
        leverage: [{ validator: (r, v, cb) => this.validateLeverage(v, cb), trigger: 'blur' }],
        positionValidDays: [positiveInt('请输入正整数仓位有效期')],
      },
    }
  },
  computed: {
    visible: { get() { return this.value }, set(v) { this.$emit('input', v) } },
    isView() { return this.mode === 'view' }, isEdit() { return this.mode === 'edit' },
    dialogTitle() { return TITLES[this.mode] },
    normalCoupon() { return isNormalCoupon(this.form.couponType) },
    tokenCoupon() { return isTokenCoupon(this.form.couponType) },
    positionCoupon() { return isPositionCoupon(this.form.couponType) },
    vipCoupon() { return isVipCoupon(this.form.couponType) },
    rateCoupon() { return hasDeductRate(this.form.couponType) },
    fundAccountDisplay() {
      const family = String(this.form.couponType || '').replace(/^(TASK|NORMAL)_/, '')
      const account = FUND_ACCOUNT_MAP[family] || {}
      const name = this.form.fundAccountName || account.name || '--'
      const type = this.form.fundAccountType || account.type
      const id = this.form.fundAccountId
      return `${name}${type ? `（账户类型 ${type}${id ? `，账户ID ${id}` : ''}）` : ''}`
    },
    leverageHint() { return `请输入${this.leverageMin}-${this.leverageMax}的整数` },
  },
  watch: { value(open) { if (open) this.init() } },
  methods: {
    integerInput(field) { this.form[field] = String(this.form[field] || '').replace(/\D/g, '') },
    amountInput() {
      let value = String(this.form.amount || '').replace(/[^\d.]/g, '')
      const parts = value.split('.'); if (parts.length > 1) value = `${parts.shift()}.${parts.join('').slice(0, 8)}`
      this.form.amount = value
    },
    validateRange(value, cb) {
      if (!this.normalCoupon) return cb()
      if (!value || value.length !== 2) return cb(new Error('请选择发放时间'))
      if (!this.isEdit && Number(value[0]) <= Date.now()) return cb(new Error('开始时间必须大于当前时间'))
      return Number(value[1]) > Number(value[0]) ? cb() : cb(new Error('结束时间必须大于开始时间'))
    },
    validateRate(value, cb) {
      if (!this.rateCoupon) return cb()
      return /^([1-9]\d?|100)$/.test(String(value || '')) ? cb() : cb(new Error('请输入1-100的整数'))
    },
    validateAmount(value, cb) {
      if (this.vipCoupon) return cb()
      if (!value || Number(value) <= 0) return cb(new Error('请输入大于0的金额'))
      if (!this.tokenCoupon && !/^[1-9]\d*$/.test(String(value))) return cb(new Error('该卡券金额须为正整数'))
      if (this.tokenCoupon && !/^\d+(\.\d{1,8})?$/.test(String(value))) return cb(new Error('最多支持8位小数'))
      cb()
    },
    validateVipLevel(value, cb) {
      if (!this.vipCoupon) return cb()
      return Number(value) > 0 ? cb() : cb(new Error('请选择VIP等级'))
    },
    validateVipValidDays(value, cb) {
      if (!this.vipCoupon) return cb()
      return /^[1-9]\d*$/.test(String(value || '')) ? cb() : cb(new Error('请输入正整数VIP有效期'))
    },
    validateLeverage(value, cb) {
      if (!this.positionCoupon) return cb()
      const n = Number(value)
      return /^\d+$/.test(String(value || '')) && n >= this.leverageMin && n <= this.leverageMax ? cb() : cb(new Error(this.leverageHint))
    },
    onTypeChange() {
      this.form.fundAccountId = null; this.form.fundAccountType = null; this.form.fundAccountName = ''
      if (!this.tokenCoupon) { this.form.coin = 'USDT'; this.coinOptions = [{ label: 'USDT', value: 'USDT' }] }
      else this.loadCoinOptions()
      if (!this.rateCoupon) this.form.deductRate = ''
      if (!this.normalCoupon) { this.form.userTypeId = null; this.form.grantRange = [] }
      if (!this.positionCoupon) { this.form.symbolIds = []; this.form.leverage = ''; this.form.positionValidDays = '' }
      if (this.$refs.form) this.$refs.form.clearValidate()
    },
    updateLeverageRange() {
      const selected = this.symbolOptions.filter((item) => this.form.symbolIds.includes(item.symbolId))
      this.leverageMin = selected.length ? Math.max(...selected.map((i) => Number(i.minLeverage || 1))) : 1
      this.leverageMax = selected.length ? Math.min(100, ...selected.map((i) => Number(i.maxLeverage || 100))) : 100
    },
    loadCoinOptions() {
      return api.enabledCoins().then((res) => {
        if (res && res.code === 0) {
          const list = Array.isArray(res.data) ? res.data : ((res.data && (res.data.items || res.data.list)) || [])
          this.coinOptions = list.map((item) => {
            const value = typeof item === 'string' ? item : (item.coin || item.coinCode || item.symbol || item.value)
            return { label: String(value || '').toUpperCase(), value: String(value || '').toUpperCase() }
          }).filter((item) => item.value)
          if (!this.coinOptions.length) this.coinOptions = [{ label: 'USDT', value: 'USDT' }]
          if (!this.coinOptions.some((item) => item.value === this.form.coin)) this.form.coin = this.coinOptions[0].value
        }
      })
    },
    init() {
      this.form = emptyForm(); this.loadOptions()
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
      if (this.mode !== 'add' && this.couponId) this.loadDetail()
    },
    loadOptions() {
      userTypeApi.options().then((res) => { if (res && res.code === 0) this.userTypeOptions = res.data || [] })
      this.coinOptions = [{ label: 'USDT', value: 'USDT' }]
      if (this.tokenCoupon) this.loadCoinOptions()
      api.positionOptions().then((res) => { if (res && res.code === 0) { this.symbolOptions = res.data || []; this.updateLeverageRange() } })
      api.vipEnums().then((res) => {
        if (res && res.code === 0) {
          const data = res.data || {}
          this.vipLevelOptions = (data.vipLevels || []).filter((item) => Number(item.code) > 0)
        }
      })
    },
    loadDetail() {
      this.loadingDetail = true
      api.detail({ id: this.couponId }).then((res) => {
        if (!res || res.code !== 0) return this.$message.error((res && res.msg) || '获取卡券详情失败')
        const d = res.data || {}; const historical = d.symbols || []
        historical.forEach((item) => { if (!this.symbolOptions.some((i) => i.symbolId === item.symbolId)) this.symbolOptions.push(item) })
        this.form = {
          ...emptyForm(), id: this.isEdit ? (d.id || this.couponId) : null,
          couponName: d.couponName || '', couponType: d.couponType || COUPON_TYPE.TASK_EXPERIENCE,
          deductRate: d.deductRate == null ? '' : String(d.deductRate), validDays: d.validDays == null ? '' : String(d.validDays),
          vipLevel: d.vipLevel == null ? null : Number(d.vipLevel), vipValidDays: d.vipValidDays == null ? '' : String(d.vipValidDays),
          coin: d.coin || 'USDT', amount: d.amount == null ? '' : String(d.amount), userTypeId: d.userTypeId || null,
          grantRange: d.grantStartTime && d.grantEndTime ? [Number(d.grantStartTime), Number(d.grantEndTime)] : [],
          symbolIds: historical.map((i) => i.symbolId), leverage: d.leverage == null ? '' : String(d.leverage),
          positionValidDays: d.positionValidDays == null ? '' : String(d.positionValidDays),
          fundAccountId: d.fundAccountId || null, fundAccountType: d.fundAccountType || null, fundAccountName: d.fundAccountName || '',
        }
        if (this.tokenCoupon) this.loadCoinOptions()
        this.updateLeverageRange()
      }).finally(() => { this.loadingDetail = false })
    },
    buildPayload() {
      const p = {
        couponName: this.form.couponName,
        couponType: this.form.couponType,
        validDays: Number(this.form.validDays),
        vipLevel: this.form.vipLevel == null || this.form.vipLevel === '' ? null : Number(this.form.vipLevel),
        vipValidDays: this.form.vipValidDays === '' ? null : Number(this.form.vipValidDays),
      }
      if (this.isEdit) p.id = this.form.id
      if (this.rateCoupon) p.deductRate = Number(this.form.deductRate)
      if (!this.vipCoupon) {
        p.amount = Number(this.form.amount)
        if (this.tokenCoupon) p.coin = this.form.coin.toUpperCase(); else p.coin = 'USDT'
      }
      if (this.normalCoupon) { p.userTypeId = this.form.userTypeId; p.grantStartTime = this.form.grantRange[0]; p.grantEndTime = this.form.grantRange[1] }
      if (this.positionCoupon) { p.symbolIds = this.form.symbolIds; p.leverage = Number(this.form.leverage); p.positionValidDays = Number(this.form.positionValidDays) }
      return p
    },
    submit() {
      this.$refs.form.validate((valid) => {
        if (!valid) return; this.submitting = true
        ;(this.isEdit ? api.update(this.buildPayload()) : api.add(this.buildPayload())).then((res) => {
          if (res && res.code === 0) { this.$message.success(this.isEdit ? '修改成功' : '添加成功'); this.$emit('success'); this.handleClose() }
          else this.$message.error((res && res.msg) || '操作失败')
        }).finally(() => { this.submitting = false })
      })
    },
    handleClose() { this.visible = false },
  },
}
</script>
